import { Button, HStack, IconButton, Input, InputGroup, Link, Stack, Text, Box } from "@chakra-ui/react"
import Big from "big.js"
import { Xmark } from "component/Icon"
import { CoolDownStatus, Staking } from "container/staking"
import { Transaction, TransactionAction } from "container/transaction"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { MotionVStack } from "../"
import { BIG_ZERO } from "../../../../constant/number"
import { XState } from "../../../../container/xstate"
import { formatNumberWithPrecision } from "../../../../util/format"
import { TxButton } from "../TxButton"
import { useToken } from "../../../../hook/useToken"
import { CONTRACT_ADDRESS } from "../../../../container/contract"

enum Step {
    Approve,
    ReadyToStake,
}

export function Stake() {
    const { stake, cooldownStatus, withdrawPendingBalance } = Staking.useContainer()
    const { approve, approveInfinity, balance, allowance: rawAllowance, queryAllowanceBySpender } = useToken(
        CONTRACT_ADDRESS.PERP_TOKEN,
        18,
    )

    const allowance = useMemo(() => {
        return rawAllowance[CONTRACT_ADDRESS.STAKE_PERP_TOKEN] || BIG_ZERO
    }, [rawAllowance])

    useEffect(() => {
        queryAllowanceBySpender(CONTRACT_ADDRESS.STAKE_PERP_TOKEN)
    }, [queryAllowanceBySpender])

    const {
        actions: { xClose },
    } = XState.useContainer()

    const { latestTx, error } = Transaction.useContainer()

    const [inputAmount, setInputAmount] = useState("")
    const [step, setStep] = useState(Step.Approve)

    const [isInputReadOnly, setIsInputReadOnly] = useState(false)

    const handleClickMax = useCallback(() => {
        setInputAmount(balance.toString())
    }, [setInputAmount, balance])

    const handleApproveInfinite = useCallback(() => {
        approveInfinity(CONTRACT_ADDRESS.STAKE_PERP_TOKEN)
    }, [approveInfinity])

    useEffect(() => {
        if (latestTx?.action === TransactionAction.APPROVE && error) {
            setIsInputReadOnly(false)
        }
    }, [latestTx, error])

    const handleClickAction = useCallback(() => {
        const amount = new Big(inputAmount)
        if (step === Step.Approve) {
            approve(CONTRACT_ADDRESS.STAKE_PERP_TOKEN, amount)
            setIsInputReadOnly(true)
        } else {
            stake(amount, xClose)
            setInputAmount("")
        }
    }, [inputAmount, step, approve, stake, xClose])

    useEffect(() => {
        if (inputAmount === "") {
            return
        }

        const amount = new Big(inputAmount)
        if (!allowance.eq(BIG_ZERO) && allowance.gte(amount)) {
            setStep(Step.ReadyToStake)
        } else if (allowance.lt(amount)) {
            setStep(Step.Approve)
        }
    }, [allowance, balance, inputAmount])

    const isInputAmountValid = useMemo(() => {
        if (inputAmount === "") {
            return true
        }

        const amount = new Big(inputAmount)
        if (amount.lte(BIG_ZERO)) {
            return false
        }

        return step === Step.Approve ? amount.gt(BIG_ZERO) : balance.gte(amount)
    }, [step, inputAmount, balance])

    const isShowingResetWarning =
        isInputAmountValid &&
        (cooldownStatus === CoolDownStatus.onGoing || cooldownStatus === CoolDownStatus.readyToWithdraw)

    return (
        <MotionVStack overflow="hidden" w="100%" spacing={5} p={7} pt={10} pb={20} position="relative">
            <IconButton
                onClick={() => xClose()}
                position="absolute"
                top={3}
                left={3}
                colorScheme="gray"
                size="xs"
                aria-label="Search database"
                borderRadius={"full"}
                icon={<Xmark boxSize="16px" />}
            />

            <Stack w="100%" spacing={2}>
                <HStack w="100%" justify="space-between">
                    <Text color="gray.400" fontSize="xs" fontWeight="bold">
                        {/* TODO: get the value from hooks */}
                        Your Wallet: {formatNumberWithPrecision(balance)} PERP
                    </Text>
                    <Button size="xs" colorScheme="gray" onClick={handleClickMax}>
                        MAX
                    </Button>
                </HStack>
                <InputGroup>
                    <Input
                        type="number"
                        value={inputAmount}
                        onChange={e => setInputAmount(e.target.value)}
                        placeholder={"Amount to Stake"}
                        errorBorderColor="red.500"
                        isInvalid={!isInputAmountValid}
                        readOnly={isInputReadOnly}
                    />
                </InputGroup>
            </Stack>
            <TxButton
                colorScheme={step === Step.Approve ? "perpetual" : "green"}
                onClick={handleClickAction}
                disabled={!isInputAmountValid || inputAmount === ""}
            >
                {step === Step.Approve ? "Approve Staking" : "Stake"}
            </TxButton>

            {isShowingResetWarning && (
                <Box p={4} borderRadius={"12px"} bgColor="orange.100">
                    <Text color="orange.700" fontSize="sm">
                        Cooldown in progress. If you stake now, the cooldown will be canceled, and the staked amount
                        will be {withdrawPendingBalance.add(new Big(inputAmount || 0)).toFixed(2)} PERP.
                    </Text>
                </Box>
            )}

            <Text color="#808080" fontSize="sm">
                You'll need to approve smart contract everytime, which costs some gas fee. You can also{" "}
                <Link color="cyan.500" onClick={handleApproveInfinite}>
                    approve infinity
                </Link>
                .
            </Text>
        </MotionVStack>
    )
}
